import { useState } from "react"
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import NotificationsIcon from "@mui/icons-material/NotificationsNoneOutlined"
import MarkReadIcon from "@mui/icons-material/DoneOutlined"
import { useNotifications } from "../context/NotificationContext"
import { relativeTime } from "../utils/format"

/** The bell. The durable record of everything that happened while you were away. */
export default function NotificationBell() {
  const [anchor, setAnchor] = useState(null)
  const { notifications, unreadCount, markAllRead, markOneRead, openNotification, refresh, loading } = useNotifications()

  const open = (event) => {
    setAnchor(event.currentTarget)
    // Refreshed on open rather than polled. Live events keep it current while the
    // tab is open; this covers the gap after a reconnect, without a request every
    // thirty seconds from every signed-in tab forever.
    refresh()
  }

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={open} size="small" aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: "calc(100vw - 32px)", maxHeight: 520 } } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography variant="h4">Notifications</Typography>
          {unreadCount > 0 ? (
            <Button size="small" onClick={markAllRead}>
              Mark all read
            </Button>
          ) : null}
        </Stack>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {loading ? "Loading…" : "Nothing yet."}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflowY: "auto" }}>
            {notifications.map((notification, index) => (
              <ListItemButton
                key={notification.id ?? `live-${notification.content_id}-${index}`}
                onClick={() => {
                  openNotification(notification)
                  setAnchor(null)
                }}
                sx={{
                  alignItems: "flex-start",
                  py: 1.25,
                  pr: 5.5,
                  bgcolor: notification.is_read ? "transparent" : "action.hover",
                  borderLeft: 3,
                  borderColor: notification.is_read ? "transparent" : "primary.main",
                  /* The tick appears on hover and on keyboard focus. Always
                     visible it is twelve ticks down a list nobody is trying to
                     tidy; never visible it cannot be reached without a mouse. */
                  "&:hover .mark-read, &:focus-within .mark-read": { opacity: 1 },
                }}
              >
                <ListItemText
                  primary={notification.title}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: notification.is_read ? 500 : 650 }}
                  secondary={
                    <>
                      {notification.body ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                          {notification.body}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary">
                        {notification.client_label ? `${notification.client_label} · ` : ""}
                        {relativeTime(notification.created_at)}
                      </Typography>
                    </>
                  }
                  secondaryTypographyProps={{ component: "div" }}
                />

                {/*
                  * Read, without opening it.
                  *
                  * "Mark all read" was the only control, which makes the bell a
                  * choice between reading everything and clearing everything —
                  * so the one somebody has already dealt with stays bold beside
                  * twelve they have not.
                  *
                  * A button inside a button is invalid, so this is a span with
                  * a role: the row is the ListItemButton and this cannot be
                  * another one. `stopPropagation` is what keeps acknowledging
                  * separate from navigating.
                  */}
                {!notification.is_read && notification.id ? (
                  <Tooltip title="Mark this one read">
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      aria-label={`Mark "${notification.title}" read`}
                      className="mark-read"
                      onClick={(event) => {
                        event.stopPropagation()
                        markOneRead(notification)
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return
                        event.preventDefault()
                        event.stopPropagation()
                        markOneRead(notification)
                      }}
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: 10,
                        display: "inline-flex",
                        p: 0.5,
                        borderRadius: "50%",
                        cursor: "pointer",
                        color: "text.secondary",
                        opacity: 0,
                        transition: "opacity 120ms ease, background-color 120ms ease",
                        "&:hover": { bgcolor: "action.selected", color: "text.primary" },
                        "&:focus-visible": {
                          opacity: 1,
                          outline: (theme) => `2px solid ${theme.palette.primary.dark}`,
                        },
                      }}
                    >
                      <MarkReadIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Tooltip>
                ) : null}
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  )
}
